/**
 * QrLivePass.js
 *
 * Layer 1 — Dynamic Temporal QR Code (TOTP, regenerates every 30 seconds offline)
 * Layer 4 — Animated Visual Anti-Video Deterrent
 *   • Animated canvas gradient background (defeats video-recording fraud via
 *     frame-rate mismatch and codec compression artifacts)
 *   • Precision HH:MM:SS.mmm clock at 60fps (proof-of-liveness signal)
 *
 * Architecture ref: docs/VISITOR_ACCESS_CONTROL_ARCHITECTURE.md sections 4, 5, 15
 * Implementation ref: docs/AI_INTEGRATION_INSTRUCTIONS.md section 5.2 State 3
 *
 * SECURITY CONTRACT:
 *   - totpSecretSeed must be passed as a prop from parent component state only.
 *   - It must NEVER be stored in localStorage, sessionStorage, cookies, or Redux.
 *   - On unmount the parent should clear its state: setTotpSecretSeed(null).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import QRCode from "react-qr-code";
import styled from "styled-components";

// ─── Styled containers ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  position: relative;
  width: 100vw;
  height: 100dvh;
  min-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const BgCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const ClockLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  text-align: center;
  padding-top: 1.5rem;
  z-index: 2;
  font-family: "Courier New", Courier, monospace;
  font-size: clamp(1.2rem, 3.5vw, 2rem);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.6);
  letter-spacing: 0.08em;
  pointer-events: none;
`;

const QrLayer = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const QrCard = styled.div`
  background: rgba(255, 255, 255, 0.93);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
`;

const CountdownBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  overflow: hidden;
`;

const CountdownFill = styled.div`
  height: 100%;
  background: ${({ pct }) =>
    pct > 0.5
      ? "rgba(72, 199, 142, 0.9)"
      : pct > 0.2
      ? "rgba(255, 183, 0, 0.9)"
      : "rgba(255, 80, 80, 0.9)"};
  width: ${({ pct }) => Math.round(pct * 100)}%;
  transition: width 0.5s linear, background 0.5s ease;
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  text-align: center;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  max-width: 280px;
  margin: 0;
`;

const VisitorLabel = styled.p`
  color: rgba(255, 255, 255, 0.97);
  font-size: 1.1rem;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  text-align: center;
  margin: 0;
`;

// ─── TOTP helpers (no external dep, pure RFC 6238 HMAC-SHA1 in browser) ──────
// When otplib is available (npm install otplib) this falls back gracefully.
// If otplib is installed, import it at the top instead.

let _otpGenerator = null;

async function loadOtplib() {
  try {
    // Dynamic import so the rest of the app doesn't break if package is absent
    const { authenticator } = await import("otplib");
    authenticator.options = { step: 30, digits: 6, algorithm: "sha1" };
    _otpGenerator = (seed) => authenticator.generate(seed);
  } catch {
    // Fallback: pure JS TOTP implementation (RFC 6238, HMAC-SHA1, 6 digits, 30s)
    _otpGenerator = (seed) => generateTotpFallback(seed);
  }
}

// ── Pure-JS TOTP fallback (no external dependency) ────────────────────────────
// Implements RFC 6238 using the Web Crypto API (available in all modern browsers).
// This is used only when `otplib` is not installed.

function base32ToBytes(base32) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  let bits = 0;
  let value = 0;
  const output = [];
  const cleanB32 = base32.toUpperCase().replace(/=+$/, "");
  for (const char of cleanB32) {
    const idx = alphabet.indexOf(char);
    if (idx < 0) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      output.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(output);
}

// Synchronous HMAC-SHA1 via a pre-cached CryptoKey approach is complex.
// Instead we cache the last generated code and update it on window intervals.
// The async version below is used once per 30-second window.
let _cachedTotpCode = "------";
let _lastTotpCounter = -1;

async function computeTotp(seed) {
  try {
    const keyBytes = base32ToBytes(seed);
    const counter = Math.floor(Date.now() / 1000 / 30);
    if (counter === _lastTotpCounter) return _cachedTotpCode;

    const counterBuffer = new ArrayBuffer(8);
    const view = new DataView(counterBuffer);
    view.setUint32(4, counter, false);

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: "HMAC", hash: "SHA-1" },
      false,
      ["sign"]
    );
    const sig = await crypto.subtle.sign("HMAC", cryptoKey, counterBuffer);
    const hmac = new Uint8Array(sig);
    const offset = hmac[hmac.length - 1] & 0x0f;
    const code =
      (((hmac[offset] & 0x7f) << 24) |
        ((hmac[offset + 1] & 0xff) << 16) |
        ((hmac[offset + 2] & 0xff) << 8) |
        (hmac[offset + 3] & 0xff)) %
      1000000;
    _cachedTotpCode = String(code).padStart(6, "0");
    _lastTotpCounter = counter;
    return _cachedTotpCode;
  } catch {
    return _cachedTotpCode;
  }
}

function generateTotpFallback(seed) {
  // Returns cached code synchronously; async computation happens in background
  const counter = Math.floor(Date.now() / 1000 / 30);
  if (counter !== _lastTotpCounter) {
    computeTotp(seed); // fire-and-forget; next render will pick up result
  }
  return _cachedTotpCode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param {string}  props.totpSecretSeed   Base32-encoded TOTP secret (from activation)
 * @param {string}  props.invitationGuid   UUID of the invitation
 * @param {string}  [props.visitorName]    Display name shown below QR
 * @param {string}  [props.validUntil]     ISO date string (invitation expiry)
 */
export default function QrLivePass({ totpSecretSeed, invitationGuid, visitorName, validUntil }) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const hueRef = useRef(0);

  const [totpCode, setTotpCode] = useState("------");
  const [clockText, setClockText] = useState("00:00:00.000");
  const [countdown, setCountdown] = useState(1); // 0..1 fraction remaining in 30s window
  const [qrContent, setQrContent] = useState("");

  // ── Layer 1: TOTP code regeneration every 30 seconds ──────────────────────
  const regenerateCode = useCallback(async () => {
    if (!totpSecretSeed) return;
    let code;
    if (_otpGenerator) {
      code = _otpGenerator(totpSecretSeed);
    } else {
      code = await computeTotp(totpSecretSeed);
    }
    setTotpCode(code);
    setQrContent(`${invitationGuid}:${code}`);
  }, [totpSecretSeed, invitationGuid]);

  useEffect(() => {
    loadOtplib().then(() => regenerateCode());
  }, [regenerateCode]);

  useEffect(() => {
    regenerateCode();
    const interval = setInterval(regenerateCode, 30_000);
    return () => clearInterval(interval);
  }, [regenerateCode]);

  // ── Layer 4a: Precision millisecond clock (proof-of-liveness) ─────────────
  useEffect(() => {
    let rafId;
    const tick = () => {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const ss = String(now.getSeconds()).padStart(2, "0");
      const ms = String(now.getMilliseconds()).padStart(3, "0");
      setClockText(`${hh}:${mm}:${ss}.${ms}`);

      // Countdown: fraction of remaining seconds in current 30s window
      const secInWindow = (Date.now() / 1000) % 30;
      setCountdown((30 - secInWindow) / 30);

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // ── Layer 4b: Animated shifting HSL canvas (anti-video deterrent) ──────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const ctx = canvas.getContext("2d");
      const w = canvas.width;
      const h = canvas.height;
      hueRef.current = (hueRef.current + 0.4) % 360;
      const h1 = hueRef.current;
      const h2 = (h1 + 60) % 360;
      const h3 = (h1 + 120) % 360;

      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, `hsl(${h1}, 80%, 40%)`);
      grad.addColorStop(0.5, `hsl(${h2}, 70%, 35%)`);
      grad.addColorStop(1, `hsl(${h3}, 85%, 30%)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Ripple circles for extra high-frequency motion
      for (let i = 0; i < 3; i++) {
        const cx = w * (0.2 + i * 0.3);
        const cy = h * (0.3 + ((hueRef.current / 360 + i * 0.33) % 1) * 0.4);
        const r = Math.min(w, h) * (0.15 + 0.05 * Math.sin(hueRef.current * 0.08 + i));
        const radGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        radGrad.addColorStop(0, `hsla(${(h1 + i * 40) % 360}, 90%, 65%, 0.35)`);
        radGrad.addColorStop(1, `hsla(${(h1 + i * 40) % 360}, 90%, 65%, 0)`);
        ctx.fillStyle = radGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  // ── Screen Wake Lock (prevent phone from dimming during QR display) ──────────
  useEffect(() => {
    let wakeLock = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch {
        // Wake lock not supported or permission denied — not critical
      }
    };
    acquire();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      wakeLock?.release().catch(() => {});
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const formatValidUntil = () => {
    if (!validUntil) return null;
    try {
      return new Date(validUntil).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  const validUntilFormatted = formatValidUntil();

  return (
    <Wrapper>
      {/* Layer 1 (z-index 1): Animated psychedelic background */}
      <BgCanvas ref={canvasRef} aria-hidden="true" />

      {/* Layer 2 (z-index 2): Precision millisecond clock */}
      <ClockLayer aria-live="off" aria-hidden="true">
        {clockText}
      </ClockLayer>

      {/* Layer 3 (z-index 3): Dynamic TOTP QR code */}
      <QrLayer>
        {visitorName && (
          <VisitorLabel>Bienvenido, {visitorName}</VisitorLabel>
        )}

        <QrCard>
          {qrContent ? (
            <QRCode
              value={qrContent}
              size={220}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
            />
          ) : (
            <div
              style={{
                width: 220,
                height: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#666",
                fontSize: "0.9rem",
              }}
            >
              Generando QR…
            </div>
          )}
        </QrCard>

        {/* Countdown bar — shows remaining time in 30s window */}
        <CountdownBar title="Tiempo restante del código QR">
          <CountdownFill pct={countdown} />
        </CountdownBar>

        <InfoText>
          Tu código QR se actualiza automáticamente cada 30 segundos.
          {validUntilFormatted && (
            <> Válido hasta {validUntilFormatted}.</>
          )}
        </InfoText>

        <InfoText style={{ fontSize: "0.75rem", opacity: 0.8 }}>
          Mantén esta pantalla visible frente al lector de la entrada.
        </InfoText>
      </QrLayer>
    </Wrapper>
  );
}
